import React from 'react';
import type { Control, UseFormRegister, FieldErrors, FormState, UseFormSetValue } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';

import { BENEFICIARY_FIELD_CONFIG } from './config';
import AutocompleteField from './components/AutocompleteField';
import IbanField from './components/IbanField';
import type { BeneficiaryField, FormValues } from './types';

interface BeneficiaryFieldsProps {
  fields: BeneficiaryField[];
  register: UseFormRegister<FormValues>;
  control: Control<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  errors: FieldErrors<FormValues>;
  touchedFields: FormState<FormValues>['touchedFields'];
  isSubmitting: boolean;
  readOnly: boolean;
  // Current form values, watched once by the modal and passed down so option
  // scoping needs no per-field subscription.
  values: FormValues;
  // Notifies the form that a field committed a new value, so fields scoped to it
  // can be cleared synchronously with the commit.
  onValueCommitted?: (fieldName: string) => void;
}

const BeneficiaryFields: React.FC<BeneficiaryFieldsProps> = ({
  fields,
  register,
  control,
  setValue,
  errors,
  touchedFields,
  isSubmitting,
  readOnly,
  values,
  onValueCommitted,
}) => {
  const { formatMessage } = useIntl();
  return (
    <Grid container spacing={2}>
      {fields.map((field) => {
        const span = BENEFICIARY_FIELD_CONFIG[field.name]?.span ?? 12;
        const isTouched = !!touchedFields[field.name];
        const errorMsg = isTouched ? ((errors[field.name]?.message as string) ?? undefined) : undefined;
        const helperText = errorMsg ?? field.helpText ?? field.subscription;

        if (field.type === 'iban') {
          // A hidden IBAN field still mounts (it assembles + submits its value)
          // but renders no preview and takes no grid slot.
          const ibanField = (
            <IbanField key={field.name} field={field} fields={fields} control={control} setValue={setValue} />
          );
          return field.hidden ? (
            ibanField
          ) : (
            <Grid key={field.name} size={span}>
              {ibanField}
            </Grid>
          );
        }

        // Any other hidden field: keep its (default) value in the form but render
        // no control.
        if (field.hidden) {
          return null;
        }

        if (field.type === 'text' || field.type === 'email') {
          return (
            <Grid key={field.name} size={span}>
              <TextField
                {...register(field.name)}
                id={field.name}
                label={field.label}
                type={field.type}
                placeholder={field.placeholder}
                fullWidth
                size="small"
                disabled={isSubmitting || readOnly || !!field.readOnly}
                required
                error={!!errorMsg}
                helperText={helperText}
              />
            </Grid>
          );
        }

        if (field.type === 'autocomplete') {
          return (
            <Grid key={field.name} size={span}>
              <AutocompleteField
                field={field}
                control={control}
                values={values}
                disabled={isSubmitting || readOnly || !!field.readOnly}
                hasError={!!errorMsg}
                helperText={helperText}
                onValueCommitted={onValueCommitted}
              />
            </Grid>
          );
        }

        if (field.type === 'select') {
          return (
            <Grid key={field.name} size={span}>
              <Controller
                name={field.name}
                control={control}
                render={({ field: f }) => (
                  <FormControl fullWidth size="small" disabled={isSubmitting || readOnly || !!field.readOnly} error={!!errorMsg}>
                    <InputLabel id={`${field.name}-label`} required>
                      {field.label}
                    </InputLabel>
                    <Select
                      {...f}
                      labelId={`${field.name}-label`}
                      label={field.label}
                      value={(f.value as string) ?? ''}
                    >
                      {field.options?.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {helperText && <FormHelperText>{helperText}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Grid>
          );
        }

        if (field.type === 'multi-select') {
          return (
            <Grid key={field.name} size={span}>
              <Controller
                name={field.name}
                control={control}
                render={({ field: f }) => (
                  <FormControl fullWidth size="small" disabled={isSubmitting || readOnly || !!field.readOnly} error={!!errorMsg}>
                    <InputLabel id={`${field.name}-label`} required>
                      {field.label}
                    </InputLabel>
                    <Select
                      {...f}
                      labelId={`${field.name}-label`}
                      multiple
                      value={(f.value as string[]) ?? []}
                      input={<OutlinedInput label={field.label} size="small" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((val) => {
                            const opt = field.options?.find((o) => o.value === val);
                            return (
                              <Chip
                                key={val}
                                label={opt?.label ?? val}
                                size="small"
                                color={isSubmitting || readOnly ? 'default' : 'primary'}
                                variant="outlined"
                                sx={{ bgcolor: 'background.paper' }}
                                onDelete={
                                  isSubmitting || readOnly
                                    ? undefined
                                    : (e) => {
                                        e.stopPropagation();
                                        f.onChange((f.value as string[]).filter((v) => v !== val));
                                      }
                                }
                                onMouseDown={(e) => e.stopPropagation()}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {(() => {
                        const remaining =
                          field.options?.filter((opt) => !(f.value as string[]).includes(opt.value)) ?? [];
                        return remaining.length > 0 ? (
                          remaining.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>
                            {formatMessage({ id: 'options.noOptionsText', defaultMessage: 'No options' })}
                          </MenuItem>
                        );
                      })()}
                    </Select>
                    {helperText && <FormHelperText>{helperText}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Grid>
          );
        }

        return null;
      })}
    </Grid>
  );
};

export default BeneficiaryFields;
